import { FieldTypes } from '../../constants/FieldTypes'
import type {
  DateTimeField,
  DropdownField,
  FieldMap,
  NumberField,
  TextAreaField,
} from '../../types/forms/fields'

export type ContactType =
  | 'phone-call'
  | 'text-message'
  | 'patient-portal-message'
  | 'in-person-without-visit'
  | 'after-hours-contact'

export type FollowUpAction =
  | 'none'
  | 'visit-scheduled'
  | 'prescription'
  | 'referral'
  | 'er-advised'
  | 'other'

// Default is 'do-not-bill'. Provider flags 'review-for-billing' when they believe
// the contact qualifies as a telephone E&M; billing team evaluates.
export type ContactBillingStatus = 'do-not-bill' | 'review-for-billing'

export const logContactField: FieldMap = {
  name: 'Log Contact',
  children: {
    date: {
      type: FieldTypes.DATETIME,
      placeholder: 'Date & Time',
      defaultToNow: true,
      isoFormat: true,
    } satisfies DateTimeField,
    contactType: {
      type: FieldTypes.DROPDOWN,
      placeholder: 'Contact Type',
      options: [
        { id: 'phone-call', text: 'Phone Call' },
        { id: 'text-message', text: 'Text Message' },
        { id: 'patient-portal-message', text: 'Patient Portal Message' },
        { id: 'in-person-without-visit', text: 'In-Person (no visit note)' },
        { id: 'after-hours-contact', text: 'After-Hours Contact' },
      ],
    } satisfies DropdownField,
    durationMinutes: {
      type: FieldTypes.NUMBER,
      placeholder: 'Duration (minutes)',
      optional: true,
      min: 1,
    } satisfies NumberField,
    clinicalSummary: {
      type: FieldTypes.TEXTAREA,
      placeholder: '1–3 sentence summary of the clinical interaction…',
    } satisfies TextAreaField,
    followUpAction: {
      type: FieldTypes.DROPDOWN,
      placeholder: 'Follow-Up Action',
      options: [
        { id: 'none', text: 'None' },
        { id: 'visit-scheduled', text: 'Visit Scheduled' },
        { id: 'prescription', text: 'Prescription' },
        { id: 'referral', text: 'Referral' },
        { id: 'er-advised', text: 'ER Advised' },
        { id: 'other', text: 'Other' },
      ],
    } satisfies DropdownField,
    followUpActionDetail: {
      type: FieldTypes.TEXTAREA,
      placeholder: 'Describe the follow-up action…',
      optional: true,
      condition: v => v?.followUpAction && v.followUpAction !== 'none',
    } satisfies TextAreaField,
    billingStatus: {
      type: FieldTypes.DROPDOWN,
      placeholder: 'Billing Status',
      options: [
        { id: 'do-not-bill', text: 'Do Not Bill' },
        { id: 'review-for-billing', text: 'Review for Billing' },
      ],
    } satisfies DropdownField,
  },
}

export interface TelephoneContact {
  assessmentId: string
  patientId: string
  practiceId: string
  /** ISO datetime string — auto-timestamped on creation, editable by provider */
  date: string
  contactType: ContactType
  /** Duration in minutes — optional per spec */
  durationMinutes?: number
  clinicalSummary: string
  followUpAction: FollowUpAction
  followUpActionDetail?: string
  billingStatus: ContactBillingStatus
  /** ISO timestamp set on creation, never overwritten — used for the 30-minute edit window. */
  createdAt?: string
}
